import { Suspense, lazy } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from "@/components/ScrollToTop";
import Layout from "@/components/Layout";

import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";

// Critical pages - loaded immediately
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Lazy load all other pages to reduce initial bundle
const Articles = lazy(() => import("./pages/Articles"));
const BrainAndMindArticle = lazy(() => import("./pages/BrainAndMindArticle"));
const DementiaArticle = lazy(() => import("./pages/DementiaArticle"));
const ParkinsonsArticle = lazy(() => import("./pages/ParkinsonsArticle"));
const ADHDArticle = lazy(() => import("./pages/ADHDArticle"));
const SleepArticle = lazy(() => import("./pages/SleepArticle"));
const AutismArticle = lazy(() => import("./pages/AutismArticle"));
const SchizophreniaArticle = lazy(() => import("./pages/SchizophreniaArticle"));
const AnxietyArticle = lazy(() => import("./pages/AnxietyArticle"));
const DepressionArticle = lazy(() => import("./pages/DepressionArticle"));
const PTSDArticle = lazy(() => import("./pages/PTSDArticle"));
const SenseOrgansArticle = lazy(() => import("./pages/SenseOrgansArticle"));
const FeelingsEmotionsArticle = lazy(() => import("./pages/FeelingsEmotionsArticle"));
const PainNociceptionArticle = lazy(() => import("./pages/PainNociceptionArticle"));
const CoffeeTeaArticle = lazy(() => import("./pages/CoffeeTeaArticle"));
const MicrobiotaArticle = lazy(() => import("./pages/MicrobiotaArticle"));

const Games = lazy(() => import("./pages/Games"));
const ReactionDashPage = lazy(() => import("./pages/games/ReactionDashPage"));
const ColourWordPage = lazy(() => import("./pages/games/ColourWordPage"));
const NumberEchoPage = lazy(() => import("./pages/games/NumberEchoPage"));
const WordSprintPage = lazy(() => import("./pages/games/WordSprintPage"));
const MemoryGridPage = lazy(() => import("./pages/games/MemoryGridPage"));
const FocusTapPage = lazy(() => import("./pages/games/FocusTapPage"));
const MathMinutePage = lazy(() => import("./pages/games/MathMinutePage"));
const MemoryCardPage = lazy(() => import("./pages/games/MemoryCardPage"));
const NumberPathPage = lazy(() => import("./pages/games/NumberPathPage"));
const TargetShiftPage = lazy(() => import("./pages/games/TargetShiftPage"));
const ChangeBlindnessPage = lazy(() => import("./pages/games/ChangeBlindnessPage"));

const Exercises = lazy(() => import("./pages/Exercises"));
const BoxBreathingPage = lazy(() => import("./pages/exercises/BoxBreathingPage"));
const AwarenessCheckInPage = lazy(() => import("./pages/exercises/AwarenessCheckInPage"));
const FiveSenseGroundingPage = lazy(() => import("./pages/exercises/FiveSenseGroundingPage"));
const PanicSOSPage = lazy(() => import("./pages/exercises/PanicSOSPage"));
const NightTimeWorryPage = lazy(() => import("./pages/exercises/NightTimeWorryPage"));
const CognitiveShufflePage = lazy(() => import("./pages/exercises/CognitiveShufflePage"));
const EisenhowerMatrixPage = lazy(() => import("./pages/exercises/EisenhowerMatrixPage"));
const AmbientStudioPage = lazy(() => import("./pages/exercises/AmbientStudioPage"));

const Questionnaires = lazy(() => import("./pages/Questionnaires"));
const PHQ9Page = lazy(() => import("./pages/questionnaires/PHQ9Page"));
const GAD7Page = lazy(() => import("./pages/questionnaires/GAD7Page"));
const PSS10Page = lazy(() => import("./pages/questionnaires/PSS10Page"));
const MAASPage = lazy(() => import("./pages/questionnaires/MAASPage"));
const PSQIPage = lazy(() => import("./pages/questionnaires/PSQIPage"));
const UCLAPage = lazy(() => import("./pages/questionnaires/UCLAPage"));
const CBIPage = lazy(() => import("./pages/questionnaires/CBIPage"));
const CRIqPage = lazy(() => import("./pages/questionnaires/CRIqPage"));

const Saved = lazy(() => import("./pages/Saved"));
const CafePage = lazy(() => import("./pages/CafePage"));
const PhotoBooth = lazy(() => import("./pages/PhotoBooth"));
const SamplerPage = lazy(() => import("./pages/SamplerPage"));
const WhatIsPage = lazy(() => import("./pages/WhatIsPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const StandardsPage = lazy(() => import("./pages/StandardsPage"));
const CLLPLandingPage = lazy(() => import("./pages/CLLPLandingPage"));
const CLLPAccessPage = lazy(() => import("./pages/CLLPAccessPage"));
const CLLPAssessmentPage = lazy(() => import("./pages/CLLPAssessmentPage"));

const MindBuddyPage = lazy(() => import("./pages/MindBuddyPage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));

const queryClient = new QueryClient();

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
      </TooltipProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Layout>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/articles" element={<Articles />} />
                <Route path="/articles/brain-and-mind" element={<BrainAndMindArticle />} />
                <Route path="/articles/dementia" element={<DementiaArticle />} />
                <Route path="/articles/parkinson" element={<ParkinsonsArticle />} />
                <Route path="/articles/adhd" element={<ADHDArticle />} />
                <Route path="/articles/sleep" element={<SleepArticle />} />
                <Route path="/articles/autism" element={<AutismArticle />} />
                <Route path="/articles/schizophrenia" element={<SchizophreniaArticle />} />
                <Route path="/articles/anxiety" element={<AnxietyArticle />} />
                <Route path="/articles/depression" element={<DepressionArticle />} />
                <Route path="/articles/ptsd" element={<PTSDArticle />} />
                <Route path="/articles/sense-organs" element={<SenseOrgansArticle />} />
                <Route path="/articles/feelings-emotions" element={<FeelingsEmotionsArticle />} />
                <Route path="/articles/pain-nociception" element={<PainNociceptionArticle />} />
                <Route path="/articles/coffee-tea" element={<CoffeeTeaArticle />} />
                <Route path="/articles/microbiota" element={<MicrobiotaArticle />} />
                <Route path="/games" element={<Games />} />
                <Route path="/games/reaction-dash" element={<ReactionDashPage />} />
                <Route path="/games/colour-word" element={<ColourWordPage />} />
                <Route path="/games/number-echo" element={<NumberEchoPage />} />
                <Route path="/games/word-sprint" element={<WordSprintPage />} />
                <Route path="/games/memory-grid" element={<MemoryGridPage />} />
                <Route path="/games/focus-tap" element={<FocusTapPage />} />
                <Route path="/games/math-minute" element={<MathMinutePage />} />
                <Route path="/games/memory-card" element={<MemoryCardPage />} />
                <Route path="/games/number-path" element={<NumberPathPage />} />
                <Route path="/games/target-shift" element={<TargetShiftPage />} />
                <Route path="/games/change-blindness" element={<ChangeBlindnessPage />} />
                <Route path="/exercises" element={<Exercises />} />
                <Route path="/exercises/box-breathing" element={<BoxBreathingPage />} />
                <Route path="/exercises/awareness-check-in" element={<AwarenessCheckInPage />} />
                <Route path="/exercises/five-sense-grounding" element={<FiveSenseGroundingPage />} />
                <Route path="/exercises/panic-sos" element={<PanicSOSPage />} />
                <Route path="/exercises/night-time-worry" element={<NightTimeWorryPage />} />
                <Route path="/exercises/cognitive-shuffle" element={<CognitiveShufflePage />} />
                <Route path="/exercises/eisenhower-matrix" element={<EisenhowerMatrixPage />} />
                <Route path="/exercises/ambient-studio" element={<AmbientStudioPage />} />
                <Route path="/questionnaires" element={<Questionnaires />} />
                <Route path="/questionnaires/phq9" element={<PHQ9Page />} />
                <Route path="/questionnaires/gad7" element={<GAD7Page />} />
                <Route path="/questionnaires/pss10" element={<PSS10Page />} />
                <Route path="/questionnaires/maas" element={<MAASPage />} />
                <Route path="/questionnaires/psqi" element={<PSQIPage />} />
                <Route path="/questionnaires/ucla" element={<UCLAPage />} />
                <Route path="/questionnaires/cbi" element={<CBIPage />} />
                <Route path="/questionnaires/criq" element={<CRIqPage />} />
                <Route path="/saved" element={<Saved />} />
                <Route path="/cafe" element={<CafePage />} />
                <Route path="/photo-booth" element={<PhotoBooth />} />
                <Route path="/sampler" element={<SamplerPage />} />
                <Route path="/what-is" element={<WhatIsPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/standards" element={<StandardsPage />} />
                <Route path="/cllp" element={<CLLPLandingPage />} />
                <Route path="/cllp/access" element={<CLLPAccessPage />} />
                <Route path="/cllp/assessment" element={<CLLPAssessmentPage />} />
                
                <Route path="/mind-buddy" element={<MindBuddyPage />} />
                <Route path="/search" element={<SearchPage />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </Layout>
        </BrowserRouter>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
